import React, { memo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Box,
} from '@mui/material';
import type {
  BalancedConfirmationResult,
  BalancedIssue,
} from '../../../../infrastructure/api/dailyShiftSlotApi';

interface BalancedConfirmDialogProps {
  open: boolean;
  preview: BalancedConfirmationResult | null;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => Promise<boolean>;
}

/** 分 → 時間表示（小数1桁、端数なしは整数） */
const toHours = (minutes: number): string => {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
};

const formatRate = (rate: number): string => `${Math.round(rate * 100)}%`;

const unfilledLabel = (issue: BalancedIssue): string => {
  const date = typeof issue.date === 'string' ? issue.date : '';
  const start = typeof issue.startTime === 'string' ? issue.startTime : '';
  const end = typeof issue.endTime === 'string' ? issue.endTime : '';
  const shortage = typeof issue.shortage === 'number' ? issue.shortage : 0;
  return `${date} ${start}-${end}（不足${shortage}名）`;
};

/**
 * バランス確定プレビューダイアログ
 *
 * 配分結果（医師別集計・問題一覧）を確認してから本確定する2段階フロー。
 * 「この内容で確定」で confirm-balanced を実行し closed → confirmed へ遷移する。
 */
const BalancedConfirmDialog: React.FC<BalancedConfirmDialogProps> = memo(({
  open,
  preview,
  isLoading,
  onClose,
  onConfirm,
}) => {
  const handleConfirm = useCallback(async () => {
    await onConfirm();
  }, [onConfirm]);

  if (!preview) {
    return null;
  }

  const unfilled = preview.issues.filter(issue => issue.type === 'unfilled');
  const imbalance = preview.issues.filter(issue => issue.type === 'imbalance');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        シフト確定プレビュー（{preview.year}年{preview.month}月）
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          希望シフトを評価（A・B・C）と充足率バランスに沿って自動配分した結果です。
          内容を確認して「この内容で確定」を押すと確定します（確定後は変更できません）。
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip label={`確定 ${preview.totals.assigned_count} 件`} color="primary" size="small" />
          <Chip
            label={`未充足枠 ${preview.totals.unfilled_slots} 件`}
            color={preview.totals.unfilled_slots > 0 ? 'warning' : 'default'}
            size="small"
          />
        </Box>

        {unfilled.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            必要人数が埋まっていない枠が {unfilled.length} 件あります:
            {' '}
            {unfilled.slice(0, 5).map(unfilledLabel).join(' / ')}
            {unfilled.length > 5 && ` ほか${unfilled.length - 5}件`}
          </Alert>
        )}

        {imbalance.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            医師間の充足率に大きな偏りがあります。必要に応じて確定後に個別調整してください。
          </Alert>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>医師</TableCell>
              <TableCell align="center">評価</TableCell>
              <TableCell align="right">固定時間</TableCell>
              <TableCell align="right">希望時間</TableCell>
              <TableCell align="right">確定時間</TableCell>
              <TableCell align="right">保証時間</TableCell>
              <TableCell align="right">充足率</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {preview.doctor_summaries.map(summary => {
              const belowGuarantee =
                summary.guaranteed_minutes > 0 &&
                summary.confirmed_minutes < summary.guaranteed_minutes;

              return (
                <TableRow key={summary.doctor_id}>
                  <TableCell>{summary.doctor_name}</TableCell>
                  <TableCell align="center">
                    {summary.priority}
                    {summary.has_fixed && summary.priority === 'A' ? '（固定）' : ''}
                  </TableCell>
                  <TableCell align="right">{toHours(summary.fixed_minutes)}</TableCell>
                  <TableCell align="right">{toHours(summary.wish_minutes)}</TableCell>
                  <TableCell align="right">{toHours(summary.confirmed_minutes)}</TableCell>
                  <TableCell align="right" sx={belowGuarantee ? { color: 'warning.main' } : undefined}>
                    {toHours(summary.guaranteed_minutes)}
                  </TableCell>
                  <TableCell align="right">{formatRate(summary.fulfillment_rate)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? '確定中...' : 'この内容で確定'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

BalancedConfirmDialog.displayName = 'BalancedConfirmDialog';

export default BalancedConfirmDialog;
